import { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Pressable } from '@fluent/ui';
import {
  detectPlatform,
  subscribeToInstallPrompt,
  triggerNativeInstall,
  getDeferredPrompt,
  type Platform,
} from './installPromptStore.js';
import './InstallModal.css';

/**
 * In-app install modal — manually opened from a button OR auto-opened
 * when the URL carries `?install=1` (the landing site deep-links here
 * for Android users so the captured `beforeinstallprompt` event can
 * actually fire on the PWA origin and trigger a real native install).
 *
 * Same visual design as the landing modal so the brand reads
 * consistently across both surfaces. Platform sections:
 *  • Android with prompt captured  → one tap → native install dialog
 *  • Android no prompt yet         → asks user to use Chrome menu
 *  • iOS Safari                    → visual Share→Add walkthrough
 *  • Installed (standalone)        → "already installed"
 *  • Desktop                       → "use phone" message
 */

interface InstallModalProps {
  open: boolean;
  onClose: () => void;
}

export function InstallModal({ open, onClose }: InstallModalProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [platform, setPlatform] = useState<Platform>(() => detectPlatform());
  const [hasPrompt, setHasPrompt] = useState<boolean>(() => getDeferredPrompt() !== null);
  const [busy, setBusy] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);

  useEffect(() => {
    // Re-detect platform when modal opens (handles delayed install state).
    if (open) {
      setPlatform(detectPlatform());
      setHasPrompt(getDeferredPrompt() !== null);
      setStatusText(null);
    }
  }, [open]);

  useEffect(() => {
    const unsub = subscribeToInstallPrompt((e) => setHasPrompt(e !== null));
    return unsub;
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', '');
      }
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const onAndroidInstall = useCallback(async () => {
    setBusy(true);
    setStatusText(null);
    const outcome = await triggerNativeInstall();
    setBusy(false);
    if (outcome === 'accepted') {
      onClose();
    } else if (outcome === 'unavailable') {
      setStatusText(
        'ბრაუზერი ვერ ხსნის სისტემურ ფანჯარას. გახსენი Chrome-ის მენიუ (⋮) → „Install app“.',
      );
    } else {
      // dismissed — user cancelled, leave modal open
      setStatusText('გაუქმდა. სცადე ისევ ან გახსენი მენიუ → „Install app“.');
    }
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className="fluent-install-modal fluent-theme-light"
      onClose={onClose}
      onClick={handleBackdropClick}
      aria-labelledby="fluent-install-modal-title"
    >
      <IconButton className="fluent-install-modal__close" label="დახურვა" onPress={onClose}>
        ×
      </IconButton>
      <h2 id="fluent-install-modal-title" className="fluent-install-modal__title">
        დააყენე აპი
      </h2>
      <p className="fluent-install-modal__sub">
        გადააქციე fluent.ge ნამდვილ აპად — ერთი შეხებით.
      </p>

      {platform === 'installed' && (
        <p className="fluent-install-modal__text">🎉 უკვე გაქვს დაყენებული. გახსენი მთავარი ეკრანიდან.</p>
      )}

      {platform === 'android' && (
        <>
          {hasPrompt ? (
            <>
              <p className="fluent-install-modal__text">
                დააჭირე ღილაკს — Chrome გაჩვენებს სისტემურ ფანჯარას, აირჩიე <strong>Install</strong>.
              </p>
              {/* Pressable, not Button — this CTA is styled to match the landing install modal; Button's 3D treatment would break the shared visual */}
              <Pressable
                className="fluent-install-modal__primary"
                onClick={onAndroidInstall}
                disabled={busy}
              >
                {busy ? 'იხსნება…' : 'დააყენე ახლა'}
              </Pressable>
            </>
          ) : (
            <p className="fluent-install-modal__text">
              გახსენი Chrome-ის მენიუ (⋮ ზედა მარჯვნივ) → <strong>Install app</strong>.
              თუ ვარიანტი არ ჩანს, შეასრულე 1 გაკვეთილი და სცადე ისევ.
            </p>
          )}
          {statusText && <p className="fluent-install-modal__hint">{statusText}</p>}
        </>
      )}

      {platform === 'ios' && (
        <>
          <ol className="fluent-install-steps">
            <li className="fluent-install-step">
              <span className="fluent-install-step__num">1</span>
              <div className="fluent-install-step__body">
                <p className="fluent-install-step__text">
                  დააჭირე <strong>Share</strong> ღილაკს ეკრანის ბოლოში.
                </p>
                <span className="fluent-install-step__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                </span>
              </div>
            </li>
            <li className="fluent-install-step">
              <span className="fluent-install-step__num">2</span>
              <div className="fluent-install-step__body">
                <p className="fluent-install-step__text">
                  აირჩიე <strong>Add to Home Screen</strong>.
                </p>
                <span className="fluent-install-step__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                </span>
              </div>
            </li>
            <li className="fluent-install-step">
              <span className="fluent-install-step__num">3</span>
              <div className="fluent-install-step__body">
                <p className="fluent-install-step__text">
                  დააჭირე <strong>Add</strong>-ს, მზადაა! 🎉
                </p>
              </div>
            </li>
          </ol>
          <p className="fluent-install-modal__hint">
            გახსენი ეს გვერდი <strong>Safari</strong>-ში (არა Chrome-ში) — თორემ Apple დამატების უფლებას არ მოგცემს.
          </p>
        </>
      )}

      {platform === 'desktop' && (
        <p className="fluent-install-modal__text">
          აპი ტელეფონისთვისაა. გადადი ტელეფონის ბრაუზერით <strong>fluent.ge</strong>-ზე და დააჭირე ღილაკს <strong>📱 დააყენე ტელეფონზე</strong>.
        </p>
      )}
    </dialog>
  );
}

/**
 * Hook that auto-opens the install modal when the URL has `?install=1`.
 * Strips the param from the URL on open so a reload doesn't re-trigger.
 */
export function useAutoOpenInstall(setOpen: (open: boolean) => void) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('install') === '1') {
      setOpen(true);
      url.searchParams.delete('install');
      window.history.replaceState({}, '', url.toString());
    }
  }, [setOpen]);
}
